import { WordToKlankConverter } from '@shared/woordkr8/WordToKlankConverter';

interface MutationTree {
  word: string;
  mutations: MutationTree[];
}

interface WordKlanken {
  word: string;
  klanken: string[];
}

export class MutationTreeBuilder {
  wordList: string[];
  wordKlankenList: WordKlanken[];

  constructor(wordList: string[]) {
    this.wordList = wordList;
    this.wordKlankenList = this.#mapWordsToWordsAndKlanken();
  }

  buildMutationTree(word: string, pathLength: number): MutationTree {
    const buildTree = (currentWord: string, depth: number, visited: Set<string>): MutationTree => {
      if (depth === 0) return { word: currentWord, mutations: [] };

      const newVisited = new Set(visited);
      newVisited.add(currentWord);

      const nextMutations = this.findMutation(currentWord, 1).filter((mutation) => !visited.has(mutation));

      return {
        word: currentWord,
        mutations: nextMutations.map((mutation) => buildTree(mutation, depth - 1, newVisited)),
      };
    };

    return buildTree(word, pathLength, new Set());
  }

  findMutation(word: string, maxMutations: number): string[] {
    const klanken = WordToKlankConverter.breakupWord(word);
    const mutationPath: string[] = [];

    this.wordKlankenList.forEach((wordObj) => {
      if (wordObj.word !== word && this.#isMutation(wordObj.klanken, klanken, maxMutations)) {
        mutationPath.push(wordObj.word);
      }
    });

    return mutationPath;
  }

  getRandomMutationPath(word: string, desiredLength: number): string[] {
    let currentWord = word;
    const mutationPath: string[] = [currentWord];
    for (let i = 0; i < desiredLength - 1; i++) {
      const nextMutations = this.findMutation(currentWord, 1);
      // prevents repetitions / cycles
      const filteredMutations = nextMutations.filter((mutation) => !mutationPath.includes(mutation));
      if (filteredMutations.length === 0) break;
      currentWord = getRandomElementFromList(filteredMutations)!;
      mutationPath.push(currentWord);
    }
    return mutationPath.reverse();
  }

  #isMutation(klanken1: string[], klanken2: string[], maxMutations: number): boolean {
    const m = klanken1.length;
    const n = klanken2.length;
    const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

    // Initialize the first row and column
    for (let i = 0; i <= m; i++) {
      dp[i][0] = i;
    }
    for (let j = 0; j <= n; j++) {
      dp[0][j] = j;
    }

    // Compute the edit distance with allowed operations: deletion, insertion, substitution
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (klanken1[i - 1] === klanken2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1];
        } else {
          dp[i][j] =
            1 +
            Math.min(
              dp[i - 1][j], // deletion
              dp[i][j - 1], // insertion
              dp[i - 1][j - 1], // substitution
            );
        }
      }
    }

    return dp[m][n] <= maxMutations;
  }

  #mapWordsToWordsAndKlanken(): WordKlanken[] {
    return this.wordList.map((word) => {
      return {
        word: word,
        klanken: WordToKlankConverter.breakupWord(word),
      };
    });
  }
}

function getRandomElementFromList<T>(list: T[]): T | undefined {
  if (list.length === 0) return undefined;
  const randomIndex = Math.floor(Math.random() * list.length);
  return list[randomIndex];
}
